<template>
  <validation-observer ref="formValidator">
    <div class="specialist-tab-content">
      <div class="row p-1 mb-4">
        <div class="col-sm">
          <validation-provider v-slot="{ errors }" rules="required">
            <custom-input
              v-model="specialistData.firstName"
              :custom-error-msg="errorMsgs.firstName"
              :disabled="disabled"
              :error="errors[0]"
              label="Nombre:"
            />
          </validation-provider>
        </div>
        <div class="col-sm">
          <custom-input
            v-model="specialistData.middleName"
            :disabled="disabled"
            label="Segundo Nombre:"
          />
        </div>
        <div class="col-sm">
          <validation-provider v-slot="{ errors }" rules="required">
            <custom-input
              v-model="specialistData.lastPaternalName"
              :custom-error-msg="errorMsgs.lastPaternalName"
              :disabled="disabled"
              :error="errors[0]"
              label="Apellido Paterno:"
            />
          </validation-provider>
        </div>
        <div class="col-sm">
          <custom-input
            v-model="specialistData.lastMaternalName"
            :disabled="disabled"
            label="Apellido Materno:"
          />
        </div>
        <div class="col-sm">
          <custom-input
            v-model="specialistData.email"
            :disabled="!userHasPermissions('provider.specialist.email.update')"
            label="Correo Electrónico:"
          />
        </div>
        <div
          v-if="this.isEducationDepartment() && !specialistData.isCseeOfficial"
          class="col-sm"
        >
          <custom-input
            v-model="specialistData.staffId"
            :disabled="disabled"
            label="Número de KRONO:"
          />
        </div>
      </div>
      <div
        class="row p-1"
        :class="{ 'mb-4': !isMedicaidDocumentationWorkflowEnabled }"
      >
        <div class="col-sm">
          <custom-input
            :disabled="true"
            label="SSN:"
            :value="maskSSN(specialistData.socialSecurityNumber)"
          />
        </div>
        <div class="col-sm">
          <validation-provider
            v-slot="{ errors }"
            :rules="isHealthDeptNoEnabled ? 'max:16' : ''"
          >
            <custom-input
              v-model="specialistData.registrationNumber"
              custom-error-msg="El campo no debe ser mayor a 16 caracteres"
              :disabled="!isHealthDeptNoEnabled"
              :error="errors[0]"
              error-class="position-absolute"
              label="Número de Registro de Salud:"
            />
          </validation-provider>
        </div>
        <div v-if="isMedicaidDocumentationWorkflowEnabled" class="col-sm">
          <div class="mt-1">
            <custom-input
              v-model="specialistData.nationalIdentifier"
              :disabled="disabled"
              label="NPI Especialista:"
              maxlength="50"
              placeholder="Ingrese el NPI Especialista"
            />
          </div>
        </div>
        <div v-if="!isMedicaidDocumentationWorkflowEnabled" class="col-sm">
          <div class="mt-4 text-right">
            <ActionButton
              v-if="immediateSave && !disabled"
              i="fas fa-save"
              text="Actualizar"
              variant="success"
              @click="updateSpecialistNames"
            />
          </div>
        </div>
      </div>
      <div
        v-if="isMedicaidDocumentationWorkflowEnabled"
        class="row p-1 mb-4 clearfix"
      >
        <div class="col-12">
          <div class="float-right">
            <ActionButton
              v-if="immediateSave && !disabled"
              i="fas fa-save"
              text="Actualizar"
              variant="success"
              @click="updateSpecialistNames"
            />
          </div>
        </div>
      </div>

      <div v-if="userHasPermissions('specialist.AtEvaluator.assign')">
        <div class="row align-items-center">
          <span class="font-weight-normal font-16 ml-3 mr-auto">
            ¿El especialista es evaluador de Asistencia Tecnológica?
          </span>
          <validation-provider v-slot="{ errors }" rules="required">
            <custom-radio
              ref="question"
              v-model="questionAnswer"
              class="custom-radio__extended p-0 mr-5"
              container-class="custom-radio-center"
              custom-error-msg="Marque sí o no."
              :disabled="disabled || haveCertifiedATLicense"
              :error="Boolean(errors[0])"
              is-question-list
              name="question-1"
              :options="questionOptions"
            />
          </validation-provider>
        </div>
        <div class="row">
          <div class="col-12">
            <div class="dropdown-divider mt-0 pb-2 w-100 line" />
          </div>
        </div>
      </div>

      <validation-provider v-slot="{ errors }" rules="required">
        <license-information-list
          ref="licInformationList"
          v-model="specialistData.licenses"
          :add-enabled="
            userHasPermissions('provider.specialist.license.add') && !disabled
          "
          :delete-enabled="
            userHasPermissions('provider.specialist.license.delete') &&
              (specialist.id == null || specialist.id <= 0) &&
              !disabled
          "
          :edit-enabled="
            userHasPermissions('provider.specialist.license.edit') && !disabled
          "
          :immediate-save="immediateSave"
          :is-a-t-evaluator="questionAnswer"
          :is-csee-official="specialistData.isCseeOfficial"
          :is-medicaid-documentation-workflow-enabled="
            isMedicaidDocumentationWorkflowEnabled
          "
          :is-related-master="specialistData.isRelatedToMaster"
          :specialist-company-id="specialistCompanyId"
          :specialist-id="specialist.id == null ? 0 : specialist.id"
          :specialist-license-document-types="specialistLicenseDocumentTypes"
          @on-license-change="emitSpecialistChange"
          @on-opened="onLicenseOpened"
        />
        <error
          v-if="errors[0]"
          :message="errorMsgs.license"
          variant="no-border"
        />
      </validation-provider>

      <validation-provider v-slot="{ errors }" rules="required">
        <service-place-information-list
          ref="spInformationList"
          v-model="specialistData.locations"
          :add-enabled="
            userHasPermissions('provider.specialist.serviceplace.add')
          "
          :can-add-locations="canAddLocations"
          :delete-enabled="
            userHasPermissions('provider.specialist.serviceplace.delete')
          "
          :edit-enabled="
            userHasPermissions('provider.specialist.serviceplace.edit')
          "
          :immediate-save="immediateSave"
          :self-assign="selfAssign()"
          :specialist="specialistData"
          :specialist-id="specialist.id == null ? 0 : specialist.id"
          @finish-add-location="saveExemptionFiles"
          @init-add-locations="showExemptionError = true"
          @on-location-change="emitSpecialistChange"
          @on-opened="onLocationOpened"
        />
        <error
          v-if="errors[0]"
          :message="errorMsgs.servicePlaceInformation"
          variant="no-border"
        />
      </validation-provider>

      <div v-if="isExemptionRequired && isExemptionParamEnabled">
        <section-title
          content="Documentos de Dispensa"
          :has-line="true"
          style="margin-top: 40px;"
        />

        <div v-for="(exemptionFile, i) in innerExemptionFiles" :key="i">
          <callout class-main="mb-2 py-3" :has-toggle="false">
            <div class="col-12 d-flex justify-content-between">
              <div>
                <span class="exemption-file__title">Documento</span>
                <p>{{ exemptionFile.name }}</p>
              </div>
              <div>
                <CustomFileInput
                  v-model="exemptionFile.file"
                  accept=".pdf"
                  add-text="Presione aquí para añadir Documento"
                  :attachment-id="exemptionFile.fileId"
                  :can-delete="!hasUploadedExemptionFiles"
                  :disabled="hasUploadedExemptionFiles"
                  display-style="normal"
                  :name="exemptionFile.fileName"
                  :on-download="() => {}"
                />
              </div>
            </div>
          </callout>
          <error
            v-if="
              showExemptionError &&
                exemptionFile.required &&
                !exemptionFile.file &&
                !exemptionFile.fileId
            "
            class="mb-3"
            message="El documento es requerido."
            variant="no-border"
          />
        </div>
      </div>
    </div>
  </validation-observer>
</template>

<script>
import LicenseInformationList from '@/views/provider/specialists/management/components/specialist-tab/components/LicenseInformationList';
import ServicePlaceInformationList from '@/views/provider/specialists/management/components/specialist-tab/components/ServicePlaceInformationList';
import {
  createSpecialist,
  updateSpecialist,
  createDeprExemption,
  getSpecialistLicenseDocumentTypes,
} from '@/modules/provider/api';
import CustomInput from '@/components/common/form/CustomInput';
import ActionButton from '@/components/common/ActionButton';
import alert from '@/mixins/alert.mixin';
import Error from '@/components/common/Error';
import Departments from '@/utils/constants/departmentsId';
import { mapState } from 'vuex';
import roles from '@/utils/constants/roles';
import SectionTitle from '@/shared/components/SectionTitle';
import CustomFileInput from '@/components/common/CustomFileInput';
import { serialize as objectToFormData } from 'object-to-formdata';
import CustomRadio from 'custom-radio';
import specialistSpecialtyTypes from '@/utils/constants/specialistSpecialtyTypes';
import moment from 'moment';
import { getSysparameterByNames as _getSysparameterByNames } from '@/modules/shared/sysparameter.api';

export default {
  name: '',
  components: {
    CustomRadio,
    LicenseInformationList,
    ServicePlaceInformationList,
    CustomInput,
    ActionButton,
    Error,
    SectionTitle,
    CustomFileInput,
  },
  mixins: [alert],
  model: {
    prop: 'specialist',
    event: 'on-specialist-change',
  },
  props: {
    specialist: {
      type: Object,
      required: true,
    },
    immediateSave: {
      type: Boolean,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: () => false,
    },
    isExemptionParamEnabled: {
      type: Boolean,
      default: false,
    },
    isExemptionRequired: {
      type: Boolean,
      default: false,
    },
    exemptionFiles: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      //MMR: Mensajes deben salir del backend.
      errorMsgs: {
        license: 'Información de Licencia(s) es requerido. (0169)',
        servicePlaceInformation:
          'Información de Lugar(es) de Servicio(s) es requerido. (0443)',
        firstName: 'Favor de ingresar el nombre. (0115)',
        lastPaternalName: 'Favor ingresar el apellido paterno. (0118)',
        email: 'El campo Correo Electrónico es requerido. (0518)',
      },
      specialistData: {},
      questionAnswer: false,
      questionOptions: [
        {
          name: 'answer1',
          label: 'Sí',
          value: true,
        },
        {
          name: 'answer2',
          label: 'No',
          value: false,
        },
      ],
      areSavedExemptionFiles: false,
      showExemptionError: false,
      innerExemptionFiles: [],
      specialistLicenseDocumentTypes: [],
      isHealthDeptNoEnabled: false,
      isMedicaidDocumentationWorkflowEnabled: false,
    };
  },
  computed: {
    ...mapState('$_user', ['user']),
    currentDate() {
      return new Date();
    },
    hasUploadedExemptionFiles() {
      const hasUploadedFiles = this.innerExemptionFiles.reduce(
        (acum, next) => !!next.fileId || acum,
        false
      );

      return hasUploadedFiles || this.areSavedExemptionFiles;
    },
    specialistCompanyId() {
      return this.specialistData?.companyId;
    },
    canAddLocations() {
      return (
        !this.isExemptionParamEnabled ||
        !this.isExemptionRequired ||
        this.areExemptionFilesValid ||
        this.hasUploadedExemptionFiles
      );
    },
    haveCertifiedATLicense() {
      return this.specialistData.licenses != null
        ? this.specialistData.licenses.some(
            (l) =>
              l.specialtyTypeId ===
                specialistSpecialtyTypes.AsistenciaTecnologica ||
              l.specialtyTypeId ===
                specialistSpecialtyTypes.EvaluadorAutorizadoAT
          )
        : false;
    },
    areExemptionFilesValid() {
      const requiredFiles = this.innerExemptionFiles.filter((x) => x.required);
      const isValid = requiredFiles.reduce(
        (acum, next) => !!next.file && acum,
        true
      );

      return isValid;
    },
  },
  watch: {
    specialist: function(newValue) {
      this.specialistData = newValue;
      if (this.specialistData.isATEvaluator != null) {
        this.$refs.question.$emit(
          'input',
          this.specialistData.isATEvaluator
            ? this.questionOptions[0]
            : this.questionOptions[1]
        );
      }
      this.specialistData.isATEvaluator = this.questionAnswer;
    },
    'specialist.firstName': {
      handler: function() {
        if (this.specialist) {
          this.specialistData.location = new Object();
          this.specialistData.firstName = this.specialist.firstName;
          this.specialistData.middleName = this.specialist.middleName;
          this.specialistData.lastPaternalName = this.specialist.lastPaternalName;
          this.specialistData.lastMaternalName = this.specialist.lastMaternalName;
          this.specialistData.staffId = this.specialist.staffId;
        }
      },
    },
    exemptionFiles: {
      deep: true,
      immediate: true,
      handler(newValue) {
        this.innerExemptionFiles = [...newValue];
      },
    },
  },
  created() {
    this.specialistData = this.specialist;
    getSpecialistLicenseDocumentTypes().then((res) => {
      this.specialistLicenseDocumentTypes = res.data;
    });

    _getSysparameterByNames({
      parameterNames: [
        'UpdatePeriodSpecialistProfileHealthDepartment',
        'MedicaidDocumentationWorkflowEnabled',
      ],
    }).then((sysParameters) => {
      sysParameters = sysParameters.data;
      var effectiveDate = moment(
        sysParameters?.find(
          (x) => x.name == 'UpdatePeriodSpecialistProfileHealthDepartment'
        ).effectiveDate
      );
      var effectiveEndDate = moment(
        sysParameters?.find(
          (x) => x.name == 'UpdatePeriodSpecialistProfileHealthDepartment'
        ).effectiveEndDate
      );

      var currentDate = moment().format('YYYY/MM/DD');

      this.isHealthDeptNoEnabled =
        moment(currentDate).isSameOrAfter(effectiveDate) &&
        moment(currentDate).isSameOrBefore(effectiveEndDate);

      this.isMedicaidDocumentationWorkflowEnabled =
        sysParameters
          ?.find((x) => x.name == 'MedicaidDocumentationWorkflowEnabled')
          ?.staticValue?.toLowerCase() == 'true' ?? false;
    });
  },
  methods: {
    maskSSN(ssn) {
      if (!ssn) return '';

      // Elimina espacios y guiones existentes
      let cleanSSN = ssn.trim().replace(/\D/g, '');

      // Verifica que tenga 9 dígitos
      if (!/^\d{9}$/.test(cleanSSN)) {
        return ssn; // Si no es válido, retorna el SSN original
      }

      // Obtiene los últimos 4 dígitos
      let last4 = cleanSSN.slice(-4);

      // Devuelve el formato enmascarado
      return `***-**-${last4}`;
    },
    async saveExemptionFiles() {
      try {
        this.showExemptionError = true;

        if (
          !this.isExemptionRequired ||
          !this.areExemptionFilesValid ||
          this.hasUploadedExemptionFiles
        )
          return;

        const formData = new FormData();
        formData.append('specialistId', this.specialistData.id);
        formData.append('companyId', this.$route.params.companyId);
        this.innerExemptionFiles.forEach((ef, i) => {
          formData.append(`files[${i}].id`, ef.id);
          formData.append(`files[${i}].file`, ef.file);
        });

        this.areSavedExemptionFiles = true;
        await createDeprExemption(formData);
      } catch (err) {
        this.ShowToast(
          'Error',
          'Ocurrió un error con los documentos de Dispensa.',
          'error'
        );
      }
    },
    departments() {
      return Departments;
    },
    isEducationDepartment() {
      const companies = this.$store.state['$_user'].companies;
      return (
        Number(this.$route.params.companyId) ===
          this.departments().EducationDepartmentId ||
        companies.some((c) => c.id === this.departments().EducationDepartmentId)
      );
    },
    selfAssign() {
      return (
        this.isEducationDepartment() || this.userRole == roles.Administrador
      );
    },
    onLicenseOpened() {
      this.$refs.spInformationList.clearEditCreate();
    },
    onLocationOpened() {
      this.$refs.licInformationList.clearEditCreate();
    },
    async insertSpecialist(validate = true) {
      const isValid = validate
        ? await this.$refs['formValidator'].validate()
        : true;

      if (!validate) {
        this.specialistData.licenses = this.specialistData.licenses ?? [];
        this.specialistData.locations = this.specialistData.locations ?? [];
      }

      if (!isValid) {
        this.ShowToast('Error', 'Información incompleta. (190803)', 'error');
        return;
      }

      if (!this.validateMedicaidIdentifiers()) {
        return;
      }

      var self = this;
      this.specialistData.isActive = true;

      const payload = objectToFormData(
        { ...this.specialistData },
        { indices: true, nullsAsUndefineds: true, dotsForObjectNotation: true }
      );

      createSpecialist(payload)
        .then((res) => {
          self.specialistData = res.data;
          self.emitSpecialistChange();
          self.$emit('on-specialist-created', self.specialistData);
          self.ShowToast(
            'Proceso Exitoso',
            'El especialista fue guardado exitosamente',
            'success'
          );
        })
        .catch((error) => {
          this.ShowToast('', error.response.data.message, 'error');
        });
    },

    async updateSpecialistNames() {
      const isValid = await this.$refs['formValidator'].validate();

      if (!isValid) {
        this.ShowToast('Error', 'Información incompleta. (190803)', 'error');
        return;
      }

      if (!this.validateMedicaidIdentifiers()) {
        return;
      }

      updateSpecialist({
        id: this.specialistData.id,
        firstName: this.specialistData.firstName,
        middleName: this.specialistData.middleName,
        lastPaternalName: this.specialistData.lastPaternalName,
        lastMaternalName: this.specialistData.lastMaternalName,
        email: this.specialistData.email,
        socialSecurityNumber: this.specialistData?.socialSecurityNumber,
        staffId: this.specialistData?.staffId,
        registrationNumber: Number(this.specialistData.registrationNumber),
        nationalIdentifier: this.specialistData.nationalIdentifier,
      })
        .then(() => {
          this.ShowToast(
            'Proceso Exitoso',
            'Los datos fueron actualizados exitosamente. ',
            'success'
          );
        })
        .catch((error) => {
          this.ShowToast('', error.response.data.message, 'error');
        });

      this.specialistData.fullName =
        (this.specialistData.firstName || '') +
        ' ' +
        (this.specialistData.middleName || '') +
        ' ' +
        (this.specialistData.lastPaternalName || '') +
        ' ' +
        (this.specialistData.lastMaternalName || '');

      this.emitSpecialistChange();
    },

    emitSpecialistChange() {
      this.$emit('on-specialist-change', this.specialistData);
    },

    validateMedicaidIdentifiers() {
      return true;
    },
  },
};
</script>

<style scoped lang="scss">
.specialist-tab-content {
  margin-bottom: 10rem;
}

.exemption-file__title {
  color: #727272;
  font-size: smaller;
}

.custom-radio__extended ::v-deep {
  .radio-button {
    background-color: #f2f2f2;
    margin-left: 8px;
  }

  .custom-radio-container {
    .bg-checked {
      background-color: #2db782 !important;
      color: white;

      .cr {
        border: 2px solid white;

        i {
          font-size: 0.653rem;
          color: white;
        }
      }
    }

    .error {
      border-color: #c9545e !important;
    }
  }
}
</style>
